import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "../../lib/utils";

export function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-zinc-200/80 dark:bg-zinc-800/80", className)}
      {...props}
    />
  );
}

export function AuthSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md p-8 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-6 animate-pulse">
        <div className="h-8 w-32 bg-slate-800 rounded-lg mx-auto" />
        <div className="space-y-4 pt-4">
          <div className="h-4 w-48 bg-slate-800 rounded mx-auto" />
          <div className="h-11 bg-slate-800 rounded-lg" />
          <div className="h-11 bg-slate-800 rounded-lg" />
          <div className="h-11 bg-blue-600/30 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export function ErrorState({ message = "Diçka shkoi keq gjatë ngarkimit.", error, onRetry }) {
  const errMsg = typeof message === "string" ? message : (error?.message || "Gabim gjatë ngarkimit të të dhënave.");
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/70 dark:bg-red-950/20 text-red-700 dark:text-red-400 my-4 shadow-sm">
      <div className="flex items-center gap-3">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span className="text-sm font-medium">{errMsg}</span>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:hover:bg-red-800/60 text-red-800 dark:text-red-200 transition-colors shrink-0"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Provo përsëri
        </button>
      )}
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="rounded-2xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 p-5 shadow-sm space-y-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-4 w-24 bg-zinc-200 dark:bg-zinc-800 rounded" />
        <div className="w-8 h-8 rounded-lg bg-zinc-200 dark:bg-zinc-800" />
      </div>
      <div className="h-7 w-20 bg-zinc-200 dark:bg-zinc-800 rounded-md" />
      <div className="h-3 w-32 bg-zinc-200 dark:bg-zinc-800 rounded" />
    </div>
  );
}

export function WeeklyStreakSkeleton() {
  return (
    <div className="rounded-2xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 p-5 shadow-sm space-y-4 animate-pulse">
      <div className="h-5 w-32 bg-zinc-200 dark:bg-zinc-800 rounded" />
      <div className="flex justify-between gap-2 pt-2">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-800" />
            <div className="w-4 h-3 bg-zinc-200 dark:bg-zinc-800 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ListRowsSkeleton({ rows = 4, height = "h-16" }) {
  return (
    <div className="space-y-3">
      {[...Array(rows)].map((_, i) => (
        <div
          key={i}
          className={cn(
            "w-full rounded-xl border border-zinc-200/70 dark:border-white/10 bg-white/60 dark:bg-zinc-900/60 p-4 animate-pulse flex items-center justify-between gap-4",
            height
          )}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-lg bg-zinc-200 dark:bg-zinc-800 shrink-0" />
            <div className="space-y-2 flex-1 max-w-md">
              <div className="h-4 w-3/4 bg-zinc-200 dark:bg-zinc-800 rounded" />
              <div className="h-3 w-1/2 bg-zinc-200 dark:bg-zinc-800 rounded" />
            </div>
          </div>
          <div className="w-20 h-7 rounded-md bg-zinc-200 dark:bg-zinc-800 shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function ProfileSkeleton() {
  return (
    <div className="rounded-2xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 p-6 shadow-sm space-y-6 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-zinc-200 dark:bg-zinc-800" />
        <div className="space-y-2">
          <div className="h-5 w-40 bg-zinc-200 dark:bg-zinc-800 rounded" />
          <div className="h-4 w-56 bg-zinc-200 dark:bg-zinc-800 rounded" />
        </div>
      </div>
      <div className="space-y-4 pt-4 border-t border-zinc-100 dark:border-zinc-800">
        <div className="h-10 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
        <div className="h-10 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
      </div>
    </div>
  );
}

export function FlashcardGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className="h-44 rounded-2xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 p-5 shadow-sm space-y-3 animate-pulse flex flex-col justify-between"
        >
          <div className="flex justify-between items-center">
            <div className="h-5 w-16 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
            <div className="w-6 h-6 bg-zinc-200 dark:bg-zinc-800 rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-6 w-32 bg-zinc-200 dark:bg-zinc-800 rounded" />
            <div className="h-4 w-48 bg-zinc-200 dark:bg-zinc-800 rounded" />
          </div>
          <div className="h-8 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

export function TableRowsSkeleton({ rows = 6 }) {
  return (
    <div className="rounded-xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 p-4 space-y-3 animate-pulse">
      <div className="h-8 bg-zinc-200/80 dark:bg-zinc-800/80 rounded-md" />
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-12 bg-zinc-200/50 dark:bg-zinc-800/50 rounded-md" />
      ))}
    </div>
  );
}
